import parse, { Element } from 'html-react-parser';
import type { ReactNode } from 'react';
import DefinitionPopover from '../components/DefinitionPopover/DefinitionPopover';

const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'br',
  'code',
  'details',
  'div',
  'em',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'popover',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'small',
  'span',
  'strong',
  'sub',
  'summary',
  'sup',
  'u',
  'ul',
]);

const DROP_CONTENT_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta']);

const ALLOWED_ATTRS_BY_TAG: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel', 'title']),
  details: new Set(['open']),
  popover: new Set(['definition']),
  img: new Set(['src', 'alt', 'width', 'height']),
};

const SAFE_IMAGE_SOURCES = new Set(['/images/excel-download.svg', '/images/excel-upload.svg']);

const ALLOWED_GLOBAL_ATTR_PREFIXES = ['aria-', 'data-'];
const ALLOWED_GLOBAL_ATTRS = new Set(['class']);
const SAFE_TARGETS = new Set(['_blank', '_self']);
const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('#') || trimmed.startsWith('/') || trimmed.startsWith('?')) return true;

  const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:)/);
  if (!schemeMatch) return true;
  return SAFE_PROTOCOLS.has(schemeMatch[1].toLowerCase());
}

function sanitizeAttributes(el: globalThis.Element, tagName: string) {
  const allowedTagAttrs = ALLOWED_ATTRS_BY_TAG[tagName] ?? new Set<string>();
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    const value = attr.value;
    const isGlobalAllowed = ALLOWED_GLOBAL_ATTR_PREFIXES.some((prefix) => name.startsWith(prefix));

    if (name.startsWith('on') || name === 'style') {
      el.removeAttribute(attr.name);
      continue;
    }
    if (!isGlobalAllowed && !ALLOWED_GLOBAL_ATTRS.has(name) && !allowedTagAttrs.has(name)) {
      el.removeAttribute(attr.name);
      continue;
    }

    if (tagName === 'a' && name === 'href' && !isSafeUrl(value)) {
      el.removeAttribute(attr.name);
      continue;
    }

    if (tagName === 'a' && name === 'target') {
      const target = value.toLowerCase();
      if (!SAFE_TARGETS.has(target)) {
        el.removeAttribute(attr.name);
      }
    }
  }

  if (tagName === 'a' && el.getAttribute('target') === '_blank') {
    const rel = (el.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
    if (!rel.includes('noopener')) rel.push('noopener');
    if (!rel.includes('noreferrer')) rel.push('noreferrer');
    el.setAttribute('rel', rel.join(' '));
  }
}

export function sanitizeHtml(input: string): string {
  if (!input) return '';

  if (typeof document === 'undefined') {
    return input.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  }

  const template = document.createElement('template');
  template.innerHTML = input;

  const visit = (node: Node): void => {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.parentNode?.removeChild(node);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const el = node as globalThis.Element;
    const tagName = el.tagName.toLowerCase();

    if (tagName === 'img' && !SAFE_IMAGE_SOURCES.has(el.getAttribute('src') ?? '')) {
      el.parentNode?.removeChild(el);
      return;
    }

    if (DROP_CONTENT_TAGS.has(tagName)) {
      el.parentNode?.removeChild(el);
      return;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      const parent = el.parentNode;
      if (!parent) return;

      const movedChildren: Node[] = [];
      while (el.firstChild) {
        const child = el.firstChild;
        parent.insertBefore(child, el);
        movedChildren.push(child);
      }
      parent.removeChild(el);
      for (const child of movedChildren) {
        visit(child);
      }
      return;
    }

    sanitizeAttributes(el, tagName);
    for (const child of Array.from(el.childNodes)) {
      visit(child);
    }
    if (tagName === 'popover') {
      const term = el.textContent || '';
      const definition = el.getAttribute('definition')?.trim();
      if (
        !definition ||
        !term.trim() ||
        el.children.length ||
        el.closest('a, button, label, summary, popover popover')
      ) {
        el.replaceWith(document.createTextNode(definition ? `${term} (${definition})` : term));
      }
    }
  };

  for (const child of Array.from(template.content.childNodes)) {
    visit(child);
  }

  return template.innerHTML;
}

export function parseSafeHtml(input: string | undefined | null): ReactNode {
  return parse(sanitizeHtml(input || ''), {
    replace: (node) => {
      if (node instanceof Element && node.name === 'popover') {
        const term = node.children
          .map((child) => (child.type === 'text' ? child.data : ''))
          .join('');
        return <DefinitionPopover term={term} definition={node.attribs.definition} />;
      }
    },
  });
}
